import { useState } from "react";
import { X, Target, AlertCircle, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { createAccount, importAccountsCsv, importIntent } from "../lib/api";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function AccountsModal({ close, onChanged }) {
  const [tab, setTab] = useState("add");

  // Add account
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [notes, setNotes] = useState("");

  // CSV paste (name,domain per line)
  const [csvText, setCsvText] = useState("");

  // Manual intent import
  const [intentText, setIntentText] = useState("");
  const [intentSource, setIntentSource] = useState("Apollo Intent tab");
  const [intentNote, setIntentNote] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const addOne = async () => {
    if (!name.trim()) return;
    setBusy(true); setError(""); setResult(null);
    try {
      await createAccount({ company_name: name.trim(), domain: domain.trim().toLowerCase(), notes: notes.trim() });
      toast.success(`${name.trim()} added to target accounts`);
      onChanged?.();
      setName(""); setDomain(""); setNotes("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not add account");
    } finally { setBusy(false); }
  };

  const importCsv = async () => {
    const rows = lines(csvText).map((line) => {
      const [company_name, domain_] = line.split(",").map((x) => (x || "").trim());
      return { company_name, domain: domain_ || "" };
    }).filter((r) => r.company_name);
    if (rows.length === 0) { setError("Add at least one line as \"Company name, domain.com\""); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const data = await importAccountsCsv({
        rows,
        mapping: { company_name: "company_name", domain: "domain" },
      });
      setResult({ kind: "accounts", ...data });
      toast.success(`${data.imported} accounts added · ${data.skipped} already on the list`);
      onChanged?.();
      setCsvText("");
    } catch (e) {
      setError("Could not import accounts");
    } finally { setBusy(false); }
  };

  const runIntentImport = async () => {
    const entries = lines(intentText).map((company_or_domain) => ({ company_or_domain, source: intentSource }));
    if (entries.length === 0) { setError("Paste at least one company name or domain, one per line"); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const data = await importIntent({ entries, note: intentNote.trim() });
      setResult({ kind: "intent", ...data });
      toast.success(`${data.tagged} prospect(s) tagged with Topic surge`);
      onChanged?.();
      setIntentText(""); setIntentNote("");
    } catch (e) {
      setError("Could not import intent signals");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" data-testid="accounts-modal">
      <div className="modal modal-wide">
        <button className="modal-close" onClick={close} data-testid="close-accounts-modal"><X size={18} /></button>
        <div className="modal-icon"><Target size={20} /></div>
        <h2>Target accounts &amp; intent</h2>
        <p>Track named facilities directly, or fold in real intent data from Apollo's Intent tab or a G2 export by hand.</p>

        <div className="filter-row" style={{ marginBottom: 16 }}>
          <button onClick={() => { setTab("add"); setResult(null); setError(""); }} className={tab === "add" ? "active-tab" : ""} data-testid="accounts-tab-add">Add account</button>
          <button onClick={() => { setTab("csv"); setResult(null); setError(""); }} className={tab === "csv" ? "active-tab" : ""} data-testid="accounts-tab-csv">Bulk paste</button>
          <button onClick={() => { setTab("intent"); setResult(null); setError(""); }} className={tab === "intent" ? "active-tab" : ""} data-testid="accounts-tab-intent">Import intent</button>
        </div>

        {tab === "add" && (
          <div className="apollo-form" style={{ gridTemplateColumns: "1fr" }}>
            <label>Company name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Harborview Medical" data-testid="account-name-input" /></label>
            <label>Domain (optional, but required to scan for signals)<input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="harborviewmed.com" data-testid="account-domain-input" /></label>
            <label>Notes (optional)<input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Referred by Sam" data-testid="account-notes-input" /></label>
          </div>
        )}

        {tab === "csv" && (
          <div className="apollo-form" style={{ gridTemplateColumns: "1fr" }}>
            <label>One account per line — "Company name, domain.com"
              <textarea rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={"Harborview Medical, harborviewmed.com\nCrescent Properties, crescentprop.com"} data-testid="accounts-csv-textarea" />
            </label>
          </div>
        )}

        {tab === "intent" && (
          <div className="apollo-form" style={{ gridTemplateColumns: "1fr" }}>
            <label>Companies or domains showing intent — one per line
              <textarea rows={6} value={intentText} onChange={(e) => setIntentText(e.target.value)} placeholder={"crescentprop.com\nHarborview Medical"} data-testid="intent-textarea" />
            </label>
            <label>Source
              <select value={intentSource} onChange={(e) => setIntentSource(e.target.value)} data-testid="intent-source-select">
                <option>Apollo Intent tab</option>
                <option>G2 buyer intent</option>
                <option>Bombora export</option>
                <option>Other</option>
              </select>
            </label>
            <label>Note (optional)<input value={intentNote} onChange={(e) => setIntentNote(e.target.value)} placeholder="Week of Sep 22 export" data-testid="intent-note-input" /></label>
          </div>
        )}

        {error && <div className="error-strip" data-testid="accounts-error"><AlertCircle size={14} /> {error}</div>}

        {result?.kind === "accounts" && (
          <div className="apollo-result" data-testid="accounts-import-result">
            <div className="apollo-stats">
              <span><b>{result.imported}</b><small>Added</small></span>
              <span><b>{result.skipped}</b><small>Already tracked</small></span>
            </div>
          </div>
        )}
        {result?.kind === "intent" && (
          <div className="apollo-result" data-testid="intent-import-result">
            <div className="apollo-stats">
              <span><b>{result.tagged}</b><small>Prospects tagged</small></span>
              <span><b>{result.unmatched.length}</b><small>Not found yet</small></span>
            </div>
            {result.unmatched.length > 0 && (
              <p style={{ fontSize: 11, marginTop: 8 }}>
                <Sparkles size={11} /> Not found: {result.unmatched.join(", ")} — add them as a target account or run a discovery first.
              </p>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button className="outline-button" onClick={close} data-testid="accounts-cancel">Close</button>
          {tab === "add" && <button className="primary-button" onClick={addOne} disabled={busy || !name.trim()} data-testid="accounts-add-submit"><Plus size={15} /> Add account</button>}
          {tab === "csv" && <button className="primary-button" onClick={importCsv} disabled={busy} data-testid="accounts-csv-submit"><Plus size={15} /> Import accounts</button>}
          {tab === "intent" && <button className="primary-button" onClick={runIntentImport} disabled={busy} data-testid="intent-import-submit"><Sparkles size={15} /> Tag intent</button>}
        </div>
      </div>
    </div>
  );
}
